import type {
  IStationControlBoard,
  IStationControlLot,
  IStationLotGate,
  IStationStepsStatus,
} from 'utils/interfaces'
import { supportsClearOverpressure, supportsMaintenanceStages } from 'utils/stationLotModel'

/** Prefer session lot, else occupied, else first configured. */
export function resolveStepsLot(steps: IStationStepsStatus | null | undefined) {
  if (!steps?.lots?.length) return null
  if (steps.session?.lot_id != null) {
    return (
      steps.lots.find(lot => lot.lot_id === steps.session!.lot_id) ?? {
        lot_id: steps.session.lot_id,
        lot_model: steps.session.lot_model,
        name: `Lot ${steps.session.lot_id}`,
        occupied: true,
        service_occupied: true,
        fleet_occupied: false,
        in_progress: false,
        release_allowed: true,
      }
    )
  }
  return steps.lots.find(lot => lot.occupied || lot.pending) ?? steps.lots[0] ?? null
}

function resolveControlLot(
  controlLots: IStationControlLot[] | undefined,
  lotId: number | null
): IStationControlLot | null {
  if (!controlLots?.length) return null
  if (lotId != null) {
    return controlLots.find(lot => lot.lot_id === lotId) ?? controlLots[0]
  }
  return controlLots.find(lot => lot.occupied || lot.pending) ?? controlLots[0]
}

/** Build one gate from the unified board — used by all Control-tab sections. */
export function buildStationLotGate(board: IStationControlBoard | null): IStationLotGate | null {
  if (!board) return null

  const stepsLot = resolveStepsLot(board.steps)
  const lotId = stepsLot?.lot_id ?? null
  const controlLot = resolveControlLot(board.control.lots, lotId)

  const occupied = Boolean(stepsLot?.occupied ?? controlLot?.occupied)
  const pending = Boolean(stepsLot?.pending ?? controlLot?.pending)
  const fleetOccupied = Boolean(stepsLot?.fleet_occupied)
  // Release/Send only for FMS-owned occupy (backend service_occupied requires session).
  // Session alone bridges MQTT→telemetry lag right after FMS Occupy.
  const session = board.steps.session
  const sessionOnLot =
    session?.lot_id != null && lotId != null && session.lot_id === lotId
  const sessionIsFms =
    sessionOnLot &&
    session?.robot_id != null &&
    Number(session.robot_id) === Number(board.steps.service_robot_id)
  const serviceOccupied = Boolean(stepsLot?.service_occupied || sessionIsFms)
  const inProgress = Boolean(stepsLot?.in_progress ?? controlLot?.in_progress)

  const stepsDisabled = board.steps.disabled_reasons || []
  const ctrlDisabled = board.control.disabled_reasons || []

  let releaseBlockedReason = stepsLot?.release_blocked_reason || null
  if (!releaseBlockedReason && pending) {
    releaseBlockedReason = 'Occupy pending — wait for OCCUPIED'
  }
  if (!releaseBlockedReason && inProgress) {
    releaseBlockedReason = 'Stage IN_PROGRESS — send CANCEL first'
  }

  let forceCancelBlockedReason = controlLot?.force_cancel_blocked_reason || null
  const forceCancelSupported =
    controlLot?.force_cancel_supported ??
    supportsMaintenanceStages(controlLot?.lot_model ?? stepsLot?.lot_model)

  let forceBlockedReason = controlLot?.force_release_blocked_reason || null
  if (!forceBlockedReason && inProgress) {
    forceBlockedReason = 'Stage IN_PROGRESS — FORCE_CANCEL or robot CANCEL first'
  }

  let clearBlockedReason = controlLot?.clear_overpressure_blocked_reason || null
  if (!clearBlockedReason && inProgress) {
    clearBlockedReason = 'Stage IN_PROGRESS — send CANCEL first'
  }

  const clearSupported =
    controlLot?.clear_overpressure_supported ??
    supportsClearOverpressure(controlLot?.lot_model ?? stepsLot?.lot_model)

  return {
    lotId,
    name: stepsLot?.name || controlLot?.name || (lotId != null ? `Lot ${lotId}` : '—'),
    occupied,
    pending,
    serviceOccupied,
    fleetOccupied,
    fleetOccupiedReason: stepsLot?.fleet_occupied_reason || null,
    robotSn: stepsLot?.robot_sn ?? controlLot?.robot_sn ?? null,
    lotModel: stepsLot?.lot_model ?? controlLot?.lot_model ?? undefined,
    inProgress,
    releaseAllowed: Boolean(stepsLot?.release_allowed) && !inProgress && !pending,
    releaseBlockedReason,
    forceCancelSupported,
    forceCancelAllowed: Boolean(controlLot?.force_cancel_allowed) && forceCancelSupported,
    forceCancelBlockedReason,
    forceReleaseAllowed: Boolean(controlLot?.force_release_allowed) && !inProgress,
    forceReleaseBlockedReason: forceBlockedReason,
    clearOverpressureAllowed: Boolean(controlLot?.clear_overpressure_allowed) && !inProgress && clearSupported,
    clearOverpressureBlockedReason: clearBlockedReason,
    clearOverpressureSupported: clearSupported,
    clearOverpressureTargets: clearSupported ? controlLot?.clear_overpressure_targets || [] : [],
    stepsEnabled: Boolean(board.steps.enabled) && stepsDisabled.length === 0,
    stepsDisabledReasons: stepsDisabled,
    ctrlEnabled: Boolean(board.control.enabled) && ctrlDisabled.length === 0,
    ctrlDisabledReasons: ctrlDisabled,
  }
}
